import { useMemo, useState } from "react";
import { Gift, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * "Unlock the next 5 for $4" banner. Hidden if:
 *  - the user is signed out
 *  - the user has fewer than 2 locked paid episodes left
 */
export default function BundleBanner({ user, paidEps, onPurchased }) {
  const [loading, setLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const lockedCount = useMemo(
    () => (paidEps || []).filter((e) => !e.unlocked).length,
    [paidEps]
  );

  if (!user || lockedCount < 2) return null;

  const dealSize = Math.min(5, lockedCount);
  const savings = (dealSize - 4).toFixed(0); // $4 for 5 → $1 off; 4-for-$4 (no discount); but minimum 2 enforced

  const onCheckout = async () => {
    setLoading(true);
    setCheckoutError(null);
    try {
      const { data } = await api.post("/checkout/bundle", { origin_url: window.location.origin });
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.href = data.url;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Checkout failed";
      toast.error(msg, { duration: Infinity, closeButton: true });
      setCheckoutError(msg);
      setLoading(false);
    }
  };

  return (
    <div
      className="relative bg-ink border border-hotpink/60 shadow-[4px_4px_0px_#FF007F] p-5 md:p-6 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      data-testid="bundle-banner"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-hotpink/15 border border-hotpink/40 flex items-center justify-center flex-shrink-0">
          <Gift size={22} className="text-hotpink" />
        </div>
        <div>
          <p className="scribble text-hotpink text-xl rotate-[-1deg] inline-block leading-none mb-1">bundle deal</p>
          <p className="brush-title text-2xl md:text-3xl text-white leading-tight">
            UNLOCK <span className="text-hotpink">{dealSize}</span> EPISODES FOR <span className="text-hotpink">$4</span>
          </p>
          <p className="font-outfit text-white/60 text-sm mt-1">
            Next {dealSize} locked episodes, instantly. {savings > 0 ? `Save $${savings} vs paying one-by-one.` : "Cheaper than buying them one by one."}
          </p>
        </div>
      </div>
      <button
        onClick={onCheckout}
        disabled={loading}
        data-testid="bundle-buy-btn"
        className="self-start md:self-auto inline-flex items-center gap-2 bg-hotpink text-white hover:bg-white hover:text-ink font-outfit font-extrabold uppercase tracking-widest text-xs px-5 py-3 shadow-[3px_3px_0px_#FFFFFF] hover:shadow-[5px_5px_0px_#FF007F] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
      >
        <Sparkles size={14} /> {loading ? "starting..." : "claim bundle — $4"}
      </button>
      {checkoutError && (
        <div
          className="w-full md:w-auto md:max-w-[300px] bg-red-900/40 border border-red-500/50 text-red-100 text-[11px] font-outfit p-2 leading-snug"
          data-testid="bundle-checkout-error"
        >
          <div className="font-bold uppercase tracking-widest text-red-300 text-[9px] mb-0.5">Checkout error</div>
          {checkoutError}
        </div>
      )}
    </div>
  );
}
